import { Box, Grid, Tab, Tabs } from '@mui/material';
import { type MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  a11yProps,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
  sizeTab,
  useTabsFormOnly,
} from '@/shared';
import {
  useFetchPaymentMethodsParams,
  useFetchPaymentMethodsV2Params,
  useUpdatePaymentMethod,
  useUpdatePaymentMethodV2,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  CustomTabPanel,
  GeneralTable,
  SingleButton,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { IPaymentMethod } from '@/shared/interfaces/erp/system-settings';
import { IPaymentMethodV2 } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';

export type PaymentMethodsPageProps = {};

const PaymentMethodsPage: React.FC<PaymentMethodsPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_formapago);
  const returnUrlPaymentMethodsPage =
    ROUTER_PATHS.administration.metodoPagoNav;
  const navigate = useNavigate();

  ///* tabs
  const { valueTab, setValueTab } = useTabsFormOnly({
    initialTabValue: 0,
  });

  ///* global state - V1
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state - V2
  const {
    filterObject: filterObjectV2,
    columnFilters: columnFiltersV2,
    setColumnFilters: setColumnFiltersV2,
  } = useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table V1
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* table V2
  const {
    globalFilter: globalFilterV2,
    pagination: paginationV2,
    searchTerm: searchTermV2,
    onChangeFilter: onChangeFilterV2,
    setPagination: setPaginationV2,
  } = useTableFilter();
  const { pageIndex: pageIndexV2, pageSize: pageSizeV2 } = paginationV2;

  ///* fetch data V1
  const {
    data: paymentMethodsRes,
    isLoading,
    isRefetching,
  } = useFetchPaymentMethodsParams({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* fetch data V2
  const {
    data: paymentMethodsV2Res,
    isLoading: isLoadingV2,
    isRefetching: isRefetchingV2,
  } = useFetchPaymentMethodsV2Params({
    page: pageIndexV2 + 1,
    page_size: pageSizeV2,
    nombre: searchTermV2,
    ...filterObjectV2,
    filterByState: false,
  });

  ///* mutations V1
  const updSate = useUpdatePaymentMethod({
    navigate,
    returnUrl: returnUrlPaymentMethodsPage,
    enableRedirect: false,
  });

  ///* mutations V2
  const updStateV2 = useUpdatePaymentMethodV2({
    navigate,
    returnUrl: returnUrlPaymentMethodsPage,
    enableRedirect: false,
  });

  ///* columns V1
  const columns: any = useMemo<MRT_ColumnDef<IPaymentMethod>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'alias',
        header: 'Nombre compuesto',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'alias'),
      },
      {
        accessorKey: 'tipo_contrato',
        header: 'Tipo de Contrato',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_contrato'),
      },
      {
        accessorKey: 'entidad_financiera',
        header: 'Entidad Financiera',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'entidad_financiera'),
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={speedState => {
                if (!hasPermission(PermissionsEnum.api_change_formapago))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_formapago!,
                      data: { estado: !!speedState } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* columns V2
  const columnsV2: any = useMemo<MRT_ColumnDef<IPaymentMethodV2>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 150,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      },
      {
        accessorKey: 'tipo_pago',
        header: 'Tipo de Pago',
        size: 150,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_pago'),
      },
      {
        accessorKey: 'entidad_financiera',
        header: 'Entidad Financiera',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'entidad_financiera'),
      },
      {
        accessorKey: 'forma_pago_legacy',
        header: 'Forma Pago V1',
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => row.original.forma_pago_legacy || '---',
      },
      {
        accessorKey: 'aplica_home',
        header: 'Aplica HOME',
        size: 120,
        enableColumnFilter: false,
        Cell: ({ row }) => (row.original.aplica_home ? 'Sí' : 'No'),
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={speedState => {
                if (!hasPermission(PermissionsEnum.api_change_formapago))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updStateV2.mutate({
                      id: row.original.id_formapago_v2!,
                      data: { estado: !!speedState } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
    ],
    []
  );

  ///* handlers V1
  const onEdit = (original: IPaymentMethod) => {
    navigate(`${returnUrlPaymentMethodsPage}/editar/${original?.id_formapago}`);
  };

  ///* handlers V2
  const onEditV2 = (original: IPaymentMethodV2) => {
    navigate(`${returnUrlPaymentMethodsPage}/v2/editar/${original?.id_formapago_v2}`);
  };

  return (
    <SingleTableBoxScene
      title="Métodos de Pago"
      enableRedirect={false}
    >
      {/* Tabs */}
      <Box sx={{ bgcolor: 'background.paper', maxWidth: sizeTab, paddingBottom: 2 }}>
        <Tabs
          value={valueTab}
          onChange={(_event: React.SyntheticEvent, newValue: number) => {
            setValueTab(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="scrollable payment methods tabs"
        >
          <Tab label="MÉTODOS DE PAGO V1" value={0} {...a11yProps(0)} />
          <Tab label="MÉTODOS DE PAGO V2" value={1} {...a11yProps(1)} />
        </Tabs>
      </Box>

      {/* =================== V1 =================== */}
      <CustomTabPanel index={valueTab} value={0}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8} md={9} lg={10}>
            <CustomSearch
              onChange={onChangeFilter}
              value={globalFilter}
              text="por nombre"
            />
          </Grid>
          {hasPermission(PermissionsEnum.api_add_formapago) && (
            <Grid item xs={12} sm={4} md={3} lg={2}>
              <SingleButton
                label="Crear"
                onClick={() => navigate(`${returnUrlPaymentMethodsPage}/crear`)}
              />
            </Grid>
          )}
        </Grid>

        <GeneralTable<IPaymentMethod>
          columns={columns}
          data={paymentMethodsRes?.data?.data || []}
          isLoading={isLoading}
          isRefetching={isRefetching}
          enableGlobalFilter={false}
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={paymentMethodsRes?.data?.count}
          enableColumnActions={hasAllPermissions([
            PermissionsEnum.api_change_formapago,
            PermissionsEnum.api_view_entidadfinanciera,
          ])}
          actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
          positionActionsColumn="first"
          canEdit={hasAllPermissions([
            PermissionsEnum.api_change_formapago,
            PermissionsEnum.api_view_entidadfinanciera,
          ])}
          onEdit={onEdit}
          canDelete={false}
        />
      </CustomTabPanel>

      {/* =================== V2 (3 Niveles) =================== */}
      <CustomTabPanel index={valueTab} value={1}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8} md={9} lg={10}>
            <CustomSearch
              onChange={onChangeFilterV2}
              value={globalFilterV2}
              text="por nombre"
            />
          </Grid>
          {hasPermission(PermissionsEnum.api_add_formapago) && (
            <Grid item xs={12} sm={4} md={3} lg={2}>
              <SingleButton
                label="Crear"
                onClick={() => navigate(`${returnUrlPaymentMethodsPage}/v2/crear`)}
              />
            </Grid>
          )}
        </Grid>

        <GeneralTable<IPaymentMethodV2>
          columns={columnsV2}
          data={paymentMethodsV2Res?.data?.data || []}
          isLoading={isLoadingV2}
          isRefetching={isRefetchingV2}
          enableGlobalFilter={false}
          enableManualFiltering={true}
          columnFilters={columnFiltersV2}
          onColumnFiltersChange={setColumnFiltersV2}
          pagination={paginationV2}
          onPaging={setPaginationV2}
          rowCount={paymentMethodsV2Res?.data?.count}
          enableColumnActions={hasAllPermissions([
            PermissionsEnum.api_change_formapago,
            PermissionsEnum.api_view_entidadfinanciera,
          ])}
          actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
          positionActionsColumn="first"
          canEdit={hasAllPermissions([
            PermissionsEnum.api_change_formapago,
            PermissionsEnum.api_view_entidadfinanciera,
          ])}
          onEdit={onEditV2}
          canDelete={false}
        />
      </CustomTabPanel>
    </SingleTableBoxScene>
  );
};

export default PaymentMethodsPage;
